import { Box, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { tokens } from '../../theme';
import type { EmptyStateProps } from './types';

/**
 * The request succeeded and there is nothing in it.
 *
 * **Distinct from the error state on purpose.** An empty phase is a fact about
 * the catalog — the content is not written yet — not a fault, so it is grey
 * rather than red, has no Try again, and does not announce itself as an alert.
 * Rendering the two the same way teaches people that a gap in the content is a
 * bug in the app.
 *
 * The designs put a "Request content →" link under this. **Not built**: there is
 * nowhere for it to go yet, and a link that leads nowhere is the thing the
 * placeholder rule exists to prevent.
 */
export const EmptyState = ({ title, message }: EmptyStateProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: 4,
      paddingBlock: 12,
      // Centred in the space left under the chrome rather than sitting just
      // beneath it. `50vh` is a floor rather than a height: a long message
      // still grows the box, and on a short viewport the content wins.
      minHeight: '50vh',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        height: 48,
        borderRadius: `${String(tokens.radius.full)}px`,
        backgroundColor: 'surface.sunken',
      }}
    >
      <InboxOutlinedIcon aria-hidden sx={{ color: 'text.meta' }} />
    </Box>

    <Box>
      <Typography variant="h3" component="h2">
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 2, maxWidth: 420 }}>
        {message}
      </Typography>
    </Box>
  </Box>
);
